import { Alert } from '@cobliteam/ui/components';
import { type QueryKey, type UseQueryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  type DeviceConfiguration,
  type DisablerEvent,
  MAXIMUM_OVERSPEEDING_THRESHOLD_IN_KMPH,
  type PageableContent,
  type UUID,
  type UpdateDeviceConfiguration,
} from '@cobliteam/common-types';

import { DeviceConfigurationRequester, type DeviceStatus, DeviceStatusRequester, DisablerRequester, GraphQLVehicleGroupsRequester, type UserPreferences } from '@cobliteam/api-lib';
import type { DeviceFragment } from '@cobliteam/api-lib/src/__generated__/graphql';
import GraphQLDeviceRequester from '@cobliteam/api-lib/src/dashboard/device/GraphQLDeviceRequester';
import type { DeviceStatusEvent } from '@cobliteam/api-lib/src/dashboard/device/definitions';

import { Environment } from '@cobliteam/react-environments';

import useUser, { QUERY_KEY as USER_QUERY_KEY } from '../useUser';

const env = Environment.getInstance().getEnvironment();
const grahpQLDeviceRequester = new GraphQLDeviceRequester();
const graphQLVehicleGroupsRequester = new GraphQLVehicleGroupsRequester();
const disablerRequester = new DisablerRequester({ host: env.herbieApiHost });
const deviceStatusRequester = new DeviceStatusRequester({ host: env.apiHost });
const deviceConfgurationRequester = new DeviceConfigurationRequester({
  host: env.apiHost,
});
const QUERY_KEY = 'DEVICES';

interface UseGetDevicesProps {
  vehicleGroupIds?: string[];
}

interface DeviceToDriverAssignmentProps {
  deviceId: string;
  driverId: string;
}

const useGetDevices = ({
  vehicleGroupIds,
  includeLastMovingStateTime = true,
  includeLastLocation = true,
  includeVehicle = true,
  includeDisablerStatus = false,
  options,
}: {
  vehicleGroupIds?: UUID[];
  includeLastMovingStateTime?: boolean;
  includeLastLocation?: boolean;
  includeVehicle?: boolean;
  includeDisablerStatus?: boolean;
  options?: Partial<UseQueryOptions<DeviceFragment[]>>;
}) => {
  const groupIds = vehicleGroupIds?.join(',');
  const queryKey = groupIds ? [QUERY_KEY, groupIds] : [QUERY_KEY];

  return useQuery<DeviceFragment[]>({
    queryKey,
    queryFn: async () => {
      const devices = await grahpQLDeviceRequester.getDevices({
        includeLastMovingStateTime,
        includeLastLocation,
        includeVehicle,
        includeDisablerStatus,
        groupIds: vehicleGroupIds,
      });
      return devices;
    },
    ...options,
  });
};

const useGetDisabledDevices = (options?: Partial<UseQueryOptions<DisablerEvent[]>>) => {
  return useQuery<DisablerEvent[]>({
    ...options,
    queryKey: [QUERY_KEY, 'FLEET_STATUS'],
    queryFn: () => disablerRequester.getFleetDevicesStatus(),
    refetchOnMount: false,
  });
};

const usePrefetchDevices = ({ enabled = false }: { enabled: boolean }) => {
  const queryClient = useQueryClient();
  const { getUser, getSession } = useUser();

  if (!enabled) return null;

  const fleetId = getSession().fleet.id;
  const preferencies: UserPreferences | undefined = queryClient.getQueryData([USER_QUERY_KEY, getUser().id]);
  const vehicleGroupIds = preferencies?.vehicleGroupIds;
  const groupIds = vehicleGroupIds?.join(',');
  const queryKey = groupIds ? [QUERY_KEY, groupIds] : [QUERY_KEY];

  queryClient.prefetchQuery({
    queryKey: [QUERY_KEY, 'GROUP', fleetId],
    queryFn: () => graphQLVehicleGroupsRequester.getVehicleGroups(),
  });

  queryClient.prefetchQuery({
    queryKey,
    queryFn: async () => {
      const devices = await grahpQLDeviceRequester.getDevices({
        groupIds: vehicleGroupIds,
      });
      return devices;
    },
  });

  return true;
};

const useGetQueryDataDevices = () => {
  const queryClient = useQueryClient();
  const getData = ({ queryKey }: { queryKey: QueryKey }) => {
    return queryClient.getQueryData(queryKey);
  };
  return getData;
};

const useSetQueryDataDevices = (updateEventsAmount: () => void) => {
  const queryClient = useQueryClient();

  const updateData = ({
    queryKey,
    data,
  }: {
    queryKey: QueryKey;
    data: DeviceStatusEvent[];
  }) => {
    return queryClient.setQueryData<DeviceFragment[]>(queryKey, (devicesVehicle) => {
      if (!devicesVehicle) return devicesVehicle;

      let devices: DeviceFragment[] = devicesVehicle;

      for (const deviceStatusEvent of data) {
        const { deviceId, ...lastLocation } = deviceStatusEvent;
        const deviceIndex = devices.findIndex((device) => device.id === deviceId);

        if (deviceIndex >= 0) {
          const foundDevice = devices[deviceIndex];
          if (!foundDevice.lastLocation || (foundDevice.lastLocation.timeInMillis && lastLocation.date > foundDevice.lastLocation.timeInMillis)) {
            if (data[0].deviceId === deviceId) {
              updateEventsAmount();
            }

            const updateDevice: DeviceFragment = {
              ...foundDevice,
              lastLocation: {
                latitude: lastLocation.coordinate.latitude,
                longitude: lastLocation.coordinate.longitude,
                timeInMillis: lastLocation.date,
                heading: lastLocation.heading,
                ignitionOn: lastLocation.ignitionOn,
                speed: lastLocation.speed,
                idleOffDurationInMillis: lastLocation.idleOffDurationInMillis || foundDevice.lastLocation?.idleOffDurationInMillis,
                idleOnDurationInMillis: lastLocation.idleOnDurationInMillis || foundDevice.lastLocation?.idleOnDurationInMillis,
                cobliIngestedAt: lastLocation.ingestionTimeInMillis || foundDevice.lastLocation?.cobliIngestedAt,
                isPlugged: lastLocation.isPlugged || foundDevice.lastLocation?.isPlugged,
                pluggedUpdateTimestamp: lastLocation.pluggedUpdateTimestamp || foundDevice.lastLocation?.pluggedUpdateTimestamp,
              },
            };

            devices = [...devices.slice(0, deviceIndex), updateDevice, ...devices.slice(deviceIndex + 1)];
          }
        }
      }

      return devices;
    });
  };

  return updateData;
};

const useGetDevicesConfiguration = () => {
  return useQuery<PageableContent<DeviceConfiguration>>({
    queryKey: [`${QUERY_KEY}`, 'DEVICES', 'CONFIGURATION'],
    queryFn: async () => {
      const devices = await deviceConfgurationRequester.getDevicesConfiguration(0, 1000);
      return devices;
    },
    refetchOnMount: false,
  });
};

const updateDevicesConfiguration = () => {
  const queryClient = useQueryClient();

  return useMutation<
    UpdateDeviceConfiguration[],
    unknown,
    {
      devicesConfiguration: UpdateDeviceConfiguration[];
    }
  >({
    mutationFn: (request) => {
      return deviceConfgurationRequester.postDevicesConfiguration(request.devicesConfiguration);
    },
    onMutate: async (request) => {
      const previousData = queryClient.getQueriesData({
        queryKey: [`${QUERY_KEY}`, 'DEVICES', 'CONFIGURATION'],
      });
      const { devicesConfiguration } = request;
      queryClient.setQueriesData(
        {
          queryKey: [`${QUERY_KEY}`, 'DEVICES', 'CONFIGURATION'],
        },
        (oldData: PageableContent<DeviceConfiguration> | undefined) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            content: oldData.content.map((deviceConfiguration) => {
              const updatedConfiguration = devicesConfiguration.find((updatedConfigurationRequest) => updatedConfigurationRequest.deviceId === deviceConfiguration.deviceId);
              if (updatedConfiguration && updatedConfiguration.speedLimitThresholdInKmh !== MAXIMUM_OVERSPEEDING_THRESHOLD_IN_KMPH) {
                return {
                  ...deviceConfiguration,
                  overSpeedingAlarmEnabled: true,
                };
              } else if (updatedConfiguration && updatedConfiguration.speedLimitThresholdInKmh === MAXIMUM_OVERSPEEDING_THRESHOLD_IN_KMPH) {
                return {
                  ...deviceConfiguration,
                  overSpeedingAlarmEnabled: false,
                };
              }
              return {
                ...deviceConfiguration,
              };
            }),
          };
        }
      );

      return { previousData };
    },
    onSuccess: () => {
      Alert.success('Configurações de áudio alteradas com sucesso');
    },
    onError: (error, variables, context: any) => {
      Alert.error('Ops! Não foi possível salvar as configurações de alerta sonoro para os veículos. Tente novamente.');
    },
  });
};

const useGetDeviceStatus = () =>
  useQuery<DeviceStatus[]>({
    queryKey: [QUERY_KEY, 'STATUS'],
    queryFn: () => deviceStatusRequester.getDevicesStatus(),
    refetchOnMount: false,
  });

const useAssignDeviceToDriver = () => {
  return useMutation({
    mutationFn: ({ deviceId, driverId }: DeviceToDriverAssignmentProps) => grahpQLDeviceRequester.assignDeviceToDriver(deviceId, driverId),
  });
};

const useUnassignDeviceToDriver = () => {
  return useMutation({
    mutationFn: ({ deviceId, driverId }: DeviceToDriverAssignmentProps) => grahpQLDeviceRequester.unassignDeviceToDriver(deviceId, driverId),
  });
};

export { QUERY_KEY };
export {
  useGetDevicesConfiguration,
  updateDevicesConfiguration,
  useGetDevices,
  useGetDisabledDevices,
  usePrefetchDevices,
  useGetQueryDataDevices,
  useSetQueryDataDevices,
  useGetDeviceStatus,
  useAssignDeviceToDriver,
  useUnassignDeviceToDriver,
};
